import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import LeadForm from "../components/LeadForm";
import FeedbackForm from "../components/FeedbackForm";

// ─────────────────────────────────────────────
// PIN GATE COMPONENT
// ─────────────────────────────────────────────
function PinGate({ correctPin, onSuccess }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin === correctPin) {
      onSuccess();
    } else {
      setError(true);
      setPin("");
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h1 className="text-2xl font-black text-white uppercase">Protected Content</h1>
          <p className="text-gray-400 mt-2">Enter PIN to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Enter PIN"
            maxLength={8}
            className={`w-full p-4 text-center text-2xl font-black tracking-widest bg-white/5 border rounded-xl text-white outline-none transition-all ${
              error ? "border-red-500 animate-pulse" : "border-white/10 focus:border-blue-500"
            }`}
            autoFocus
          />
          {error && <p className="text-red-500 text-center text-sm font-bold">Incorrect PIN</p>}
          <button
            type="submit"
            className="w-full py-4 bg-blue-600 hover:bg-blue-500 rounded-2xl text-white font-black uppercase tracking-widest transition-all"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PIXEL FIRING
// ─────────────────────────────────────────────
function firePixels(qr) {
  if (qr.meta_pixel_id) {
    const script = document.createElement("script");
    script.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${qr.meta_pixel_id}');fbq('track','PageView');`;
    document.head.appendChild(script);
  }
  if (qr.google_pixel_id) {
    const gScript = document.createElement("script");
    gScript.src = `https://www.googletagmanager.com/gtag/js?id=${qr.google_pixel_id}`;
    gScript.async = true;
    document.head.appendChild(gScript);
    const inline = document.createElement("script");
    inline.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${qr.google_pixel_id}');gtag('event','qr_scan');`;
    document.head.appendChild(inline);
  }
}

// ─────────────────────────────────────────────
// SCAN LOGGER — awaited before every redirect
// Geo has 4s timeout. Log inserts regardless.
// Atomic RPC increment — no race condition.
// ─────────────────────────────────────────────
async function logScan(qr, code) {
  const ua = navigator.userAgent;
  const device = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? "Mobile" : "Desktop";
  const browser =
    ua.includes("Edg") ? "Edge"
    : ua.includes("Chrome") ? "Chrome"
    : ua.includes("Firefox") ? "Firefox"
    : ua.includes("Safari") ? "Safari"
    : ua.includes("OPR") || ua.includes("Opera") ? "Opera"
    : "Other";

  try {
    // keepalive: true is the critical flag — tells the browser to complete
    // this request even if the page navigates away. Works on Safari iOS 13+.
    await fetch("/api/log-scan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        qr_id: qr.id,
        user_id: qr.user_id,
        code,
        ua,
        device,
        browser,
        hour: new Date().getHours(),
      }),
    });
  } catch (e) {
    console.warn("Log scan failed:", e.message);
  }
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
export default function RedirectHandler() {
  const { code } = useParams();
  const [qrData, setQrData] = useState(null);
  const [view, setView] = useState("loading");
  const [couponCode, setCouponCode] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (!code || hasProcessed.current) return;
    hasProcessed.current = true;

    const handleScan = async () => {
      try {
        // 1. Fetch QR record
        const { data: qr, error } = await supabase
          .from("qr_codes")
          .select("*")
          .eq("code", code)
          .single();

        if (error || !qr) { setView("error"); return; }

        // 2. Check expiry / active status
        if (!qr.is_active) { setView("expired"); return; }
        if (qr.expires_at && new Date(qr.expires_at) < new Date()) { setView("expired"); return; }
        if (qr.max_scans && qr.total_scans >= qr.max_scans) { setView("expired"); return; }

        // 3. Fire retargeting pixels (non-blocking — ok if lost on redirect)
        firePixels(qr);

        // 4. Set QR data in state
        setQrData(qr);

        // 5. A/B TESTING
        if (qr.ab_testing && qr.ab_url_a && qr.ab_url_b) {
          const variant = Math.random() < 0.5 ? "A" : "B";
          const target = variant === "A" ? qr.ab_url_a : qr.ab_url_b;
          await logScan(qr, code); // LOG BEFORE REDIRECT
          window.location.replace(target);
          return;
        }

        // 6. PIN PROTECTION
        if (qr.pin_code) {
          await logScan(qr, code);
          setView("pin");
          return;
        }

        // 7. COUPON MODE
        if (qr.coupon_mode) {
          await logScan(qr, code);
          setView("coupon");
          fetch("/api/get-coupon", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ qr_id: qr.id }),
          })
            .then((r) => r.json())
            .then((data) => setCouponCode(data.code || "NO-MORE-COUPONS"))
            .catch(() => setCouponCode("ERROR"));
          return;
        }

        // 8. Route by QR mode
        const mode = (qr.qr_mode || "direct").toLowerCase().trim();

        // LEAD FORM
        if (mode === "leads" || mode === "lead" || mode === "lead_gen") {
          await logScan(qr, code);
          setView("leads");
          return;
        }

        // FEEDBACK FORM
        if (mode === "feedback" || mode === "review" || mode === "review_filter") {
          await logScan(qr, code);
          setView("feedback");
          return;
        }

        // WHATSAPP — interstitial
        if (mode === "whatsapp") {
          const wa = qr.settings?.whatsapp || {};
          if (!wa.number) { setView("error"); return; }
          const waUrl = `https://wa.me/${wa.number.replace(/\D/g, "")}?text=${encodeURIComponent(wa.message || "")}`;
          await logScan(qr, code);
          setQrData({ ...qr, interstitialAction: { type: "whatsapp", url: waUrl, label: "Open WhatsApp" } });
          setView("interstitial");
          return;
        }

        // VCARD — interstitial
        if (mode === "vcard") {
          const vc = qr.settings?.vcard || {};
          const escape = (str) => String(str || "")
            .replace(/\\/g, "\\\\").replace(/;/g, "\\;")
            .replace(/,/g, "\\,").replace(/\n/g, "\\n");
          const fullName = `${vc.firstName || ""} ${vc.lastName || ""}`.trim() || "Contact";
          const lines = [
            "BEGIN:VCARD", "VERSION:3.0",
            `N:${escape(vc.lastName)};${escape(vc.firstName)};;;`,
            `FN:${escape(fullName)}`,
          ];
          if (vc.mobile) lines.push(`TEL;TYPE=CELL,VOICE:${escape(vc.mobile)}`);
          if (vc.email) lines.push(`EMAIL;TYPE=INTERNET:${escape(vc.email)}`);
          if (vc.address) lines.push(`ADR;TYPE=WORK:;;${escape(vc.address)};;;;`);
          if (vc.website) lines.push(`URL:${escape(vc.website)}`);
          lines.push("END:VCARD");
          await logScan(qr, code);
          setQrData({ ...qr, interstitialAction: { type: "vcard", vcardText: lines.join("\r\n"), fileName: fullName, label: "Save Contact" } });
          setView("interstitial");
          return;
        }

        // WIFI — interstitial
        if (mode === "wifi") {
          const escape = (str) => String(str || "").replace(/([\;,:"])/g, "\\$1");
          const sec = qr.wifi_security === "None" || qr.wifi_security === "nopass" ? "nopass" : (qr.wifi_security || "WPA");
          let wifiString = `WIFI:T:${sec};S:${escape(qr.wifi_ssid)};`;
          if (sec !== "nopass") wifiString += `P:${escape(qr.wifi_password)};`;
          if (qr.wifi_hidden) wifiString += `H:true;`;
          await logScan(qr, code);
          setQrData({ ...qr, interstitialAction: { type: "wifi", wifiString, ssid: qr.wifi_ssid, label: "Connect to WiFi" } });
          setView("interstitial");
          return;
        }

        // UPI — interstitial
        if (mode === "upi") {
          if (!qr.upi_id) { setView("error"); return; }
          let upiUrl = `upi://pay?pa=${encodeURIComponent(qr.upi_id)}`;
          if (qr.upi_name) upiUrl += `&pn=${encodeURIComponent(qr.upi_name)}`;
          if (qr.upi_amount) upiUrl += `&am=${qr.upi_amount}`;
          upiUrl += `&cu=INR`;
          await logScan(qr, code);
          setQrData({ ...qr, interstitialAction: { type: "upi", url: upiUrl, label: "Pay via UPI" } });
          setView("interstitial");
          return;
        }

        // MENU
        if (mode === "menu") {
          await logScan(qr, code);
          window.location.replace(`https://qr.digitoly.com/menu/${code}`);
          return;
        }

        // DIRECT + FALLBACK
        await logScan(qr, code);
        window.location.replace(qr.destination_url || "https://www.digitoly.com");

      } catch (err) {
        console.error("RedirectHandler error:", err);
        setView("error");
      }
    };

    handleScan();
  }, [code]);

  // ─── VIEWS ───────────────────────────────────

  if (view === "loading") return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white" />
    </div>
  );

  if (view === "error") return (
    <div className="min-h-screen bg-black flex items-center justify-center text-white">
      <div className="text-center">
        <div className="text-4xl mb-4">❌</div>
        <p className="font-black uppercase">QR code not found.</p>
      </div>
    </div>
  );

  if (view === "expired") return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6 text-center">
      <div>
        <div className="text-4xl mb-4">⏰</div>
        <h1 className="text-2xl font-black text-white uppercase">QR Code Expired</h1>
        <p className="text-gray-400 mt-2">This QR code is no longer active.</p>
      </div>
    </div>
  );

  if (view === "coupon") return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-3xl p-8 space-y-6 text-center">
        <div className="text-4xl">🎟️</div>
        <h1 className="text-2xl font-black text-white uppercase">Your Coupon</h1>
        <p className="text-gray-400">Exclusive discount just for you</p>
        {couponCode ? (
          <div className="space-y-4">
            <div className="bg-black p-6 rounded-xl border border-white/10">
              <p className="text-3xl font-black text-white tracking-widest font-mono">{couponCode}</p>
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(couponCode)}
              className="w-full py-4 bg-blue-600 hover:bg-blue-500 rounded-2xl text-white font-black uppercase tracking-widest transition-all"
            >
              Copy Code
            </button>
            {qrData?.destination_url && (
              <button
                onClick={() => window.location.replace(qrData.destination_url)}
                className="w-full py-4 bg-white/5 border border-white/10 rounded-2xl text-white font-black uppercase tracking-widest transition-all hover:bg-white/10"
              >
                Shop Now →
              </button>
            )}
          </div>
        ) : (
          <div className="animate-pulse text-gray-400">Loading coupon...</div>
        )}
      </div>
    </div>
  );

  if (view === "pin") return (
    <PinGate
      correctPin={qrData.pin_code}
      onSuccess={() => {
        const mode = (qrData.qr_mode || "").toLowerCase().trim();
        if (mode === "leads" || mode === "lead" || mode === "lead_gen") { setView("leads"); return; }
        if (mode === "feedback" || mode === "review" || mode === "review_filter") { setView("feedback"); return; }
        if (mode === "menu") { window.location.replace(`https://qr.digitoly.com/menu/${code}`); return; }
        window.location.replace(qrData.destination_url || "https://www.digitoly.com");
      }}
    />
  );

  if (view === "interstitial") {
    const action = qrData?.interstitialAction;
    if (!action) return null;

    const handleTap = () => {
      if (action.type === "whatsapp" || action.type === "upi") {
        window.location.href = action.url;
      } else if (action.type === "wifi") {
        window.location.href = action.wifiString;
      } else if (action.type === "vcard") {
        const blob = new Blob([action.vcardText], { type: "text/vcard" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${action.fileName}.vcf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    };

    const icons = { whatsapp: "💬", upi: "💳", wifi: "📶", vcard: "📇" };

    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="text-6xl mb-6">{icons[action.type]}</div>
          <h2 className="text-white text-xl font-black uppercase italic tracking-tighter mb-2">
            {qrData?.name || "Tap to Continue"}
          </h2>
          <p className="text-gray-500 text-[10px] uppercase tracking-widest mb-8">
            {action.type === "wifi"
              ? `Connect to "${action.ssid}"`
              : action.type === "upi"
              ? "Opens your UPI payment app"
              : action.type === "vcard"
              ? "Saves contact to your phone"
              : "One tap to continue"}
          </p>
          <button
            onClick={handleTap}
            className="w-full py-5 bg-blue-600 hover:bg-blue-500 rounded-2xl text-white font-black uppercase tracking-widest transition-all active:scale-95"
          >
            {action.label} →
          </button>
        </div>
      </div>
    );
  }

  if (view === "leads") return <LeadForm qrId={code} ownerId={qrData?.user_id} />;
  if (view === "feedback") return <FeedbackForm qrId={code} ownerId={qrData?.user_id} />;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white font-black uppercase tracking-widest animate-pulse">
        Redirecting...
      </div>
    </div>
  );
}
